import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoFixHigh as AutoIcon,
  DragIndicator as DragIcon,
  Route as RouteIcon,
  RemoveCircleOutline as RemoveIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  formatDistance,
  formatDuration,
  getOrderId,
  getRouteColor,
} from '../../constants/routePlanning';
import { UNASSIGNED } from '../../hooks/useMultiRouteBuilder';

const customerName = (order) =>
  order?.deliveryAddress?.userName || order?.customerName || order?.customer_name || 'Customer';

const SortableAutoRow = ({ order, index, color, onRemove }) => {
  const id = getOrderId(order);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  return (
    <ListItem
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.7 : 1,
        background: '#fff',
        borderBottom: '1px solid #f0f0f0',
      }}
      secondaryAction={
        <Tooltip title="Remove from route">
          <IconButton edge="end" size="small" onClick={() => onRemove(id)}>
            <RemoveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
      sx={{ pr: 6 }}
    >
      <ListItemIcon sx={{ minWidth: 30 }} {...attributes} {...listeners}>
        <DragIcon fontSize="small" sx={{ cursor: 'grab', color }} />
      </ListItemIcon>
      <ListItemText
        primary={`${index + 1}. #${id}`}
        secondary={customerName(order)}
        primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
        secondaryTypographyProps={{ variant: 'caption' }}
      />
    </ListItem>
  );
};

/**
 * Right-panel content for the map's Auto-plan mode: plan controls + one card per route + the
 * unassigned pool. All route-building state lives in the useMultiRouteBuilder hook passed in.
 */
const AutoRoutePanel = ({ multiRoute, disabled = false }) => {
  const [routeCount, setRouteCount] = useState(2);
  const [maxStops, setMaxStops] = useState('');
  const [maxTime, setMaxTime] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const plottableCount = Object.keys(multiRoute.ordersById).filter(
    (id) =>
      multiRoute.ordersById[id]?.deliveryAddress?.latLong &&
      multiRoute.ordersById[id].deliveryAddress.latLong.lat !== 0
  ).length;
  const maxAllowed = Math.min(multiRoute.maxRoutes, Math.max(1, plottableCount));

  const handlePlan = () => {
    const count = Math.max(1, Math.min(Number(routeCount) || 1, maxAllowed));
    multiRoute.autoPlan({
      routeCount: count,
      maxStopsPerRoute: maxStops ? Number(maxStops) : undefined,
      maxDurationMinutes: maxTime ? Number(maxTime) : undefined,
    });
  };

  const unassignedOrders = multiRoute.unassignedIds
    .map((id) => multiRoute.ordersById[id])
    .filter(Boolean);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Plan controls */}
      <Box sx={{ px: 2, pt: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Auto plan routes
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mb: 1.5,
            '& .MuiFormHelperText-root': {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              mx: 0,
            },
          }}
        >
          <TextField
            label="Routes"
            type="number"
            size="small"
            fullWidth
            value={routeCount}
            onChange={(e) => setRouteCount(e.target.value)}
            inputProps={{ min: 1, max: maxAllowed }}
            helperText={`Riders available today. One route each (1–${maxAllowed}).`}
          />
          <TextField
            label="Max stops"
            type="number"
            size="small"
            fullWidth
            value={maxStops}
            onChange={(e) => setMaxStops(e.target.value)}
            inputProps={{ min: 1 }}
            helperText="Most orders on one route. Leave empty for no limit."
          />
          <TextField
            label="Max time"
            type="number"
            size="small"
            fullWidth
            value={maxTime}
            onChange={(e) => setMaxTime(e.target.value)}
            inputProps={{ min: 1 }}
            helperText="Longest route in minutes. Leave empty for no limit."
          />
        </Box>
        <Button
          fullWidth
          variant="contained"
          onClick={handlePlan}
          disabled={disabled || multiRoute.planning || plottableCount === 0}
          startIcon={multiRoute.planning ? <CircularProgress size={18} color="inherit" /> : <AutoIcon />}
          sx={{ mt: 1 }}
        >
          {multiRoute.planning ? 'Planning…' : multiRoute.routes.length ? 'Re-plan' : 'Auto Plan'}
        </Button>
        {multiRoute.planError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {multiRoute.planError}
          </Alert>
        )}
      </Box>

      {/* Route cards */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
        {multiRoute.routes.length === 0 && !multiRoute.planning && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            Set the number of routes and click Auto Plan. Orders will be split across that many
            riders automatically.
          </Typography>
        )}

        {multiRoute.routes.map((route, index) => {
          const color = getRouteColor(route.colorIndex).hex;
          const orders = route.orderedIds.map((id) => multiRoute.ordersById[id]).filter(Boolean);
          return (
            <Paper
              key={route.id}
              variant="outlined"
              sx={{ mb: 1.5, overflow: 'hidden', borderColor: color }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  bgcolor: color,
                  color: '#fff',
                }}
              >
                <RouteIcon fontSize="small" />
                <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 700 }}>
                  Route {index + 1} · {route.orderedIds.length} stop
                  {route.orderedIds.length === 1 ? '' : 's'}
                </Typography>
              </Box>
              <Box sx={{ px: 1, py: 0.5, display: 'flex', gap: 2 }}>
                <Typography variant="caption">
                  {route.estimating ? '…' : formatDistance(route.totalDistanceMeters)}
                </Typography>
                <Typography variant="caption">
                  {route.estimating ? '…' : formatDuration(route.totalDurationSeconds)}
                </Typography>
              </Box>
              {orders.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={({ active, over }) =>
                    over && multiRoute.reorderWithin(route.id, active.id, over.id)
                  }
                >
                  <SortableContext items={route.orderedIds} strategy={verticalListSortingStrategy}>
                    <List dense disablePadding>
                      {orders.map((order, i) => (
                        <SortableAutoRow
                          key={getOrderId(order)}
                          order={order}
                          index={i}
                          color={color}
                          onRemove={(id) => multiRoute.moveOrder(id, UNASSIGNED)}
                        />
                      ))}
                    </List>
                  </SortableContext>
                </DndContext>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1 }}>
                  No stops. Move orders here from another route or the unassigned list.
                </Typography>
              )}
            </Paper>
          );
        })}

        {unassignedOrders.length > 0 && (
          <>
            <Divider sx={{ my: 1 }}>
              <Chip
                label={`${unassignedOrders.length} unassigned`}
                color="warning"
                size="small"
              />
            </Divider>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              These orders are not on a route. They will be left out when you create routes. Plan
              again with a higher route count or looser limits to include them.
            </Typography>
            <List dense disablePadding>
              {unassignedOrders.map((order) => {
                const id = getOrderId(order);
                return (
                  <ListItem
                    key={id}
                    sx={{ border: '1px dashed', borderColor: 'warning.main', mb: 0.5, borderRadius: 1 }}
                  >
                    <ListItemText
                      primary={`#${id}`}
                      secondary={customerName(order)}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
      </Box>
    </Box>
  );
};

export default AutoRoutePanel;
